import type { HttpMethod, QueryParamsType } from '@blufin/types';
import type { HmacVerifyData } from '../Hmac';

export interface IHmacUtils {
  getContentLength: (body: any) => number;
  getCurrentTimeInEpoch: () => number;
  getTimeElapsedInMillis: (epoch: number) => number;
  standardizeMethod: (method: HttpMethod) => string;
  standardizeQueryParams: (queryParams: QueryParamsType) => string;
  standardizeRequest: (data: HmacVerifyData, timestamp: number) => string;
  standardizeUrl: (url: string) => string;
}

export const HmacUtils: IHmacUtils = {
  /**
   * Calculates the content length of the given body.
   * Uses TextEncoder for browsers and Buffer.byteLength for Node.js.
   * @param body - The body whose content length is to be calculated.
   */
  getContentLength(body: any): number {
    if (body === undefined || body === null || typeof body === 'function') return 0;
    const jsonString = JSON.stringify(body);
    /** Use TextEncoder if available (modern browsers and Node.js >= 11). */
    if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(jsonString).length;
    /** Fallback to Buffer.byteLength for older Node.js environments. */
    if (typeof Buffer !== 'undefined') return Buffer.byteLength(jsonString, 'utf8');
    /** Manual UTF-8 byte length calculation as last resort. */
    let byteLength = 0;
    for (let i = 0; i < jsonString.length; i++) {
      const code = jsonString.charCodeAt(i);
      if (code < 0x80) byteLength += 1;
      else if (code < 0x800) byteLength += 2;
      else if (code < 0xd800 || code >= 0xe000) byteLength += 3;
      else {
        /** Handle surrogate pairs (emoji and rare Unicode characters). */
        /** Check if this is a high surrogate followed by a low surrogate. */
        if (i + 1 < jsonString.length) {
          const nextCode = jsonString.charCodeAt(i + 1);
          if (nextCode >= 0xdc00 && nextCode < 0xe000) {
            /** Valid surrogate pair - skip the low surrogate. */
            i++;
            byteLength += 4;
            continue;
          }
        }
        /** Invalid surrogate - treat as 3-byte character. */
        byteLength += 3;
      }
    }
    return byteLength;
  },
  /**
   * Gets the current time in epoch milliseconds.
   */
  getCurrentTimeInEpoch() {
    return Date.now();
  },
  /**
   * Gets the time elapsed in milliseconds since the given epoch time.
   * @param epoch - The epoch time in milliseconds.
   */
  getTimeElapsedInMillis(epoch: number) {
    return Date.now() - epoch;
  },
  /**
   * Standardizes the HTTP method by trimming whitespace and converting to lowercase.
   * @param method - HTTP method (GET, POST, etc.)
   */
  standardizeMethod(method: HttpMethod): string {
    return method.trim().toLowerCase();
  },
  /**
   * Standardizes the query parameters by sorting keys, trimming whitespace, and converting to lowercase.
   * @param queryParams - The query parameters to be standardized.
   */
  standardizeQueryParams(queryParams: QueryParamsType): string {
    const standardize = (obj: any): any => {
      const keys = Object.keys(obj).sort();
      const result: { [key: string]: any } = {};
      for (const key of keys) {
        const value = obj[key];
        const standardizedKey = key.trim().toLowerCase();
        if (typeof value === 'object' && value !== null && !Array.isArray(value))
          result[standardizedKey] = standardize(value);
        else result[standardizedKey] = (value ?? '').toString().trim().toLowerCase();
      }
      return result;
    };
    return JSON.stringify(standardize(queryParams));
  },
  /**
   * Standardizes the request data for HMAC computation.
   * @param param0 - Request data object
   * @param param0.contentLength - Content length of the request body
   * @param param0.method - HTTP method (GET, POST, etc.)
   * @param param0.queryParams - Query parameters (optional)
   * @param param0.url - Request URL (optional)
   * @param timestamp - Timestamp in milliseconds
   */
  standardizeRequest({ contentLength, method, queryParams, url }: HmacVerifyData, timestamp: number): string {
    let output = HmacUtils.standardizeMethod(method);
    if (url) output += ' ' + HmacUtils.standardizeUrl(url);
    output += ' ' + contentLength;
    /** Filter out undefined/null query param values before standardization. */
    const filtered = queryParams
      ? Object.fromEntries(Object.entries(queryParams).filter(([, v]) => v !== undefined && v !== null))
      : undefined;
    if (filtered && Object.keys(filtered).length > 0)
      output += ' ' + HmacUtils.standardizeQueryParams(filtered as QueryParamsType);
    return output.trim() + ' ' + timestamp;
  },
  /**
   * Standardizes the URL by trimming whitespace, converting to lowercase, removing extra slashes, and ensuring it starts with a single slash.
   * @param url - The URL to be standardized.
   */
  standardizeUrl(url: string): string {
    let output: string;
    try {
      const parsedUrl = new URL(url);
      output = parsedUrl.pathname;
    } catch {
      output = url;
    }
    /** Combine: trim, lowercase, remove whitespace, normalize slashes in 3 passes (down from 4). */
    output = output
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/^\/+|\/+$/g, '')
      .replace(/\/+/g, '/');
    return output === '' ? '/' : '/' + output;
  }
};
